import React, { useEffect, useMemo, useState } from 'react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { getObras } from '../services/obraService';
import { getEmpresa } from '../services/empresaService';
import { getFichaRupturaData, getFichaRupturaCalendario } from '../services/fichaRupturaService';
import { getSessionClientId } from '../services/authSession';
import ExpandableSearchFilter from '../components/ExpandableSearchFilter';
import FichaRupturaPDF from '../components/FichaRupturaPDF';
import usePersonalizacaoRelatorio from '../hooks/usePersonalizacaoRelatorio';
import { normalizeDateOnly, formatDateBr } from '../utils/formatters';

// ---- helpers ----------------------------------------------------------------

// Normaliza uma obra da API para { id, nome, idEmpresa }.
function normalizeObra(item) {
  return {
    id: String(item.idObra || item.id_obra || item.id || ''),
    nome: item.nomeObra || item.nome_obra || item.nome || '-',
    idEmpresa: String(
      item.idEmpresa || item.id_empresa || item.empresa?.idEmpresa || item.empresa?.id_empresa || ''
    ),
    idCliente: String(
      item.idCliente || item.id_cliente || item.cliente?.idCliente || item.cliente?.id_cliente || item.cliente?.id || ''
    ),
    idContatoTecnico: String(
      item.idContatoTecnico || item.contatoTecnico?.idContato || item.ContatoTecnico?.IdContato || ''
    ),
  };
}

// Normaliza uma empresa da API para { id, nome, idUsuarioAssinatura1, idUsuarioAssinatura2 }.
function normalizeEmpresa(item) {
  return {
    id: String(item.idEmpresa || item.id_empresa || item.id || ''),
    nome: item.razaoSocial || item.razao_social || item.nome || item.nomeFantasia || '-',
    idUsuarioAssinatura1: String(item.idUsuarioAssinatura1 || item.id_usuario_assinatura_1 || item.usuarioAssinatura1?.idUsuario || ''),
    idUsuarioAssinatura2: String(item.idUsuarioAssinatura2 || item.id_usuario_assinatura_2 || item.usuarioAssinatura2?.idUsuario || ''),
  };
}

// Normaliza um corpo de prova vindo de api/fichaRuptura para a tela (nota fiscal já resolvida no backend).
function normalizeCorpoProva(item) {
  const nfPrincipal = String(item.numrNotaFiscalPrincipal || '').trim();
  const nfSecundaria = String(item.numrNotaFiscalSecundaria || '').trim();
  const notaFiscal = [nfPrincipal, nfSecundaria].find((v) => v && v !== '0') || '-';

  return {
    id: String(item.idCorpoProva || ''),
    etiqueta: item.idenObraEtiqueta || '-',
    idadeRuptura: item.idadeRuptura ?? '',
    dataRuptura: normalizeDateOnly(item.dataRuptura || ''),
    dataConcretagem: normalizeDateOnly(item.dataConcretagem || ''),
    notaFiscal,
    obra: item.obra?.nomeObra || '-',
    fck: item.fck ?? null,
    cimento: item.cimento || null,
    idCaminhao: item.idCaminhao ?? null,
    idTipoCorpoProva: item.idTipoCorpoProva ?? null,
  };
}

// ---- calendário -------------------------------------------------------------

const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];
const DAY_LABELS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

// Zero-pad de um número para 2 dígitos.
function padDate(n) { return String(n).padStart(2, '0'); }
// Monta uma string YYYY-MM-DD a partir de ano/mês(0-based)/dia.
function toDateStr(year, month, day) {
  return `${year}-${padDate(month + 1)}-${padDate(day)}`;
}

// Grade de um mês do calendário; marca a data de ruptura e mostra a contagem de CP por dia.
function MonthGrid({ year, month, dataRuptura, cpCountByDate, onChange, todayStr }) {
  const firstDayOfWeek = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < firstDayOfWeek; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="w-full max-w-sm">
      <div className="grid grid-cols-7 text-center">
        {DAY_LABELS.map((d, i) => (
          <div
            key={i}
            className="pb-2 text-[11px] font-medium uppercase tracking-wide text-slate-400"
          >
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 text-center">
        {cells.map((day, i) => {
          if (day === null) return <div key={`e-${i}`} />;
          const dateStr = toDateStr(year, month, day);
          const count = cpCountByDate[dateStr] || 0;
          const isSelected = dateStr === dataRuptura;
          const isToday = dateStr === todayStr;
          const isDisabled = count === 0;

          return (
            <button
              key={day}
              type="button"
              disabled={isDisabled}
              onClick={() => { if (!isDisabled) onChange(dateStr); }}
              className={`flex flex-col items-center justify-center rounded-lg py-1.5 transition ${
                isSelected
                  ? 'bg-[#0e7490] text-white'
                  : isDisabled
                  ? 'cursor-not-allowed'
                  : 'hover:bg-[#0e7490]/10'
              }`}
            >
              <span
                className={`text-[13px] font-medium leading-none ${
                  isSelected
                    ? 'text-white'
                    : isToday
                    ? 'text-[#0e7490] underline underline-offset-2 decoration-[#0e7490]'
                    : count > 0
                    ? 'text-slate-800'
                    : 'text-slate-400'
                }`}
              >
                {day}
              </span>
              {count > 0 ? (
                <span
                  className={`mt-0.5 text-[10px] leading-none font-medium ${
                    isSelected ? 'text-white/80' : 'text-[#0e7490]'
                  }`}
                >
                  {count}
                </span>
              ) : (
                <span className="mt-0.5 text-[10px] leading-none invisible select-none">0</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Calendário de seleção da data de ruptura (navega entre meses e exibe o MonthGrid).
function CalendarioRuptura({ dataRuptura, onChange, onClear, cpCountByDate, loadingCalendar }) {
  const today = new Date();
  const todayStr = toDateStr(today.getFullYear(), today.getMonth(), today.getDate());

  const [currentMonth, setCurrentMonth] = useState(() => {
    if (dataRuptura) {
      const [y, m] = dataRuptura.split('-');
      return new Date(Number(y), Number(m) - 1, 1);
    }
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const nextMonthDate = new Date(year, month + 1, 1);
  const nextYear = nextMonthDate.getFullYear();
  const nextMonth = nextMonthDate.getMonth();

  return (
    <div className="w-full max-w-sm">
      <div className="mb-3 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setCurrentMonth(new Date(year, month - 1, 1))}
          className="inline-flex h-7 w-7 items-center justify-center rounded-full text-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
          aria-label="Mês anterior"
        >
          ‹
        </button>
        <span className="text-sm font-semibold text-slate-700">
          {MONTH_NAMES[month]} {year}
        </span>
        <button
          type="button"
          onClick={() => setCurrentMonth(new Date(year, month + 1, 1))}
          className="inline-flex h-7 w-7 items-center justify-center rounded-full text-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
          aria-label="Próximo mês"
        >
          ›
        </button>
      </div>

      {loadingCalendar ? (
        <div className="flex items-center justify-center py-10">
          <div className="h-4 w-48 animate-pulse rounded bg-slate-100" />
        </div>
      ) : (
        <MonthGrid
          year={year}
          month={month}
          dataRuptura={dataRuptura}
          cpCountByDate={cpCountByDate}
          onChange={onChange}
          todayStr={todayStr}
        />
      )}
    </div>
  );
}

// ---- checkbox ---------------------------------------------------------------

// Item de checkbox reutilizável (usado nas listas de empresas/obras).
function CheckboxItem({ label, checked, onChange, disabled }) {
  return (
    <label
      className={`flex items-center gap-3 rounded-lg px-3 py-2.5 transition ${
        disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50'
      }`}
    >
      <span
        className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border transition ${
          checked && !disabled
            ? 'border-[#0e7490] bg-[#0e7490]'
            : disabled
            ? 'border-slate-200 bg-slate-100'
            : 'border-slate-300 bg-white'
        }`}
      >
        {checked && !disabled ? (
          <svg className="h-2.5 w-2.5 text-white" fill="none" viewBox="0 0 10 8">
            <path
              d="M1 4l3 3 5-6"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : null}
      </span>
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <span className={`text-sm leading-tight ${disabled ? 'text-slate-300' : 'text-slate-700'}`}>
        {label}
      </span>
    </label>
  );
}

// ---- page -------------------------------------------------------------------

// Tela de Ficha de Ruptura: filtra corpos de prova por empresa/obra/data e gera o PDF da ficha (NBR 5739).
function FichaRupturaPage() {
  // Cliente logado: toda a ficha (calendario e listagens) fica restrita a ele.
  const sessionClientId = String(getSessionClientId() || '');

  const [dataRuptura, setDataRuptura] = useState('');
  const [selectedEmpresaIds, setSelectedEmpresaIds] = useState([]);
  const [selectedObraIds, setSelectedObraIds] = useState([]);
  const [obras, setObras] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [corposProva, setCorposProva] = useState([]);
  const [loadingCPs, setLoadingCPs] = useState(false);
  const [query, setQuery] = useState('');
  const [cpCountByDate, setCpCountByDate] = useState({});
  const [obraIdsByDate, setObraIdsByDate] = useState({});
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Carrega obras e empresas na montagem
  useEffect(() => {
    let isMounted = true;
    Promise.allSettled([getObras(), getEmpresa()]).then(([obrasResult, empresasResult]) => {
      if (!isMounted) return;
      if (obrasResult.status === 'fulfilled' && Array.isArray(obrasResult.value)) {
        let normalizedObras = obrasResult.value.map(normalizeObra).filter((o) => o.id);
        // Restringe as obras ao cliente logado (admin/sem cliente ve todas).
        if (sessionClientId) normalizedObras = normalizedObras.filter((o) => o.idCliente === sessionClientId);
        setObras(normalizedObras);
      }
      if (empresasResult.status === 'fulfilled' && Array.isArray(empresasResult.value)) {
        setEmpresas(empresasResult.value.map(normalizeEmpresa).filter((e) => e.id));
      }
    });
    return () => { isMounted = false; };
  }, []);

  // Carrega a contagem de CPs por data (agregada no backend) para montar o calendário.
  // Para o cliente logado, conta apenas os CPs das obras dele (senao mostra o total geral).
  const obraIdsKey = obras.map((o) => o.id).sort().join(',');
  useEffect(() => {
    // Cliente logado ainda sem obras carregadas: nada a contar.
    if (sessionClientId && obras.length === 0) {
      setCpCountByDate({});
      setObraIdsByDate({});
      return;
    }
    let isMounted = true;
    setLoadingCalendar(true);
    const idObras = sessionClientId ? obras.map((o) => o.id) : undefined;
    getFichaRupturaCalendario({ idObras })
      .then((data) => {
        if (!isMounted || !Array.isArray(data)) return;
        const counts = {};
        const obrasByDate = {};
        data.forEach((item) => {
          const dateStr = normalizeDateOnly(item.data || '');
          if (!dateStr) return;
          counts[dateStr] = item.quantidade || 0;
          obrasByDate[dateStr] = new Set((item.idsObra || []).map(String));
        });
        setCpCountByDate(counts);
        setObraIdsByDate(obrasByDate);
      })
      .finally(() => {
        if (isMounted) setLoadingCalendar(false);
      });
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [obraIdsKey]);

  // Recarrega CPs quando a data de ruptura ou as obras selecionadas mudam (filtro já aplicado no backend)
  const selectedObraKey = [...selectedObraIds].sort().join(',');
  useEffect(() => {
    if (selectedObraIds.length === 0 || !dataRuptura) {
      setCorposProva([]);
      return;
    }
    let isMounted = true;
    setLoadingCPs(true);
    getFichaRupturaData({ dataRuptura, idObras: selectedObraIds })
      .then((data) => {
        if (!isMounted) return;
        const list = Array.isArray(data) ? data : data?.items || [];
        setCorposProva(list.map(normalizeCorpoProva).filter((c) => c.id));
      })
      .catch(() => { if (isMounted) setCorposProva([]); })
      .finally(() => { if (isMounted) setLoadingCPs(false); });
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedObraKey, dataRuptura]);

  const obrasOrdenadas = useMemo(
    () => [...obras].sort((a, b) => a.nome.localeCompare(b.nome)),
    [obras]
  );
  const empresasOrdenadas = useMemo(
    () => [...empresas].sort((a, b) => a.nome.localeCompare(b.nome)),
    [empresas]
  );

  // Obras e empresas que têm CP na data selecionada
  const obrasComCpNaData = useMemo(
    () => (dataRuptura ? (obraIdsByDate[dataRuptura] || new Set()) : null),
    [dataRuptura, obraIdsByDate]
  );

  const empresasComCpNaData = useMemo(() => {
    if (!obrasComCpNaData) return null;
    return new Set(obras.filter((o) => obrasComCpNaData.has(o.id)).map((o) => o.idEmpresa));
  }, [obrasComCpNaData, obras]);

  // Obras visíveis: com CP na data + pertencentes às empresas selecionadas (se houver)
  const obrasVisiveis = useMemo(() => {
    let list = obrasOrdenadas;
    if (obrasComCpNaData) list = list.filter((o) => obrasComCpNaData.has(o.id));
    if (selectedEmpresaIds.length > 0) list = list.filter((o) => selectedEmpresaIds.includes(o.idEmpresa));
    return list;
  }, [obrasOrdenadas, obrasComCpNaData, selectedEmpresaIds]);

  // Empresas visíveis: com CP na data + dona de obra selecionada (se houver)
  const empresasVisiveis = useMemo(() => {
    let list = empresasOrdenadas;
    if (empresasComCpNaData) list = list.filter((e) => empresasComCpNaData.has(e.id));
    if (selectedObraIds.length > 0) {
      const empresasDasObras = new Set(
        obras.filter((o) => selectedObraIds.includes(o.id)).map((o) => o.idEmpresa)
      );
      list = list.filter((e) => empresasDasObras.has(e.id));
    }
    return list;
  }, [empresasOrdenadas, empresasComCpNaData, selectedObraIds, obras]);

  const nomeEmpresasSelecionadas = useMemo(
    () => empresas.filter((e) => selectedEmpresaIds.includes(e.id)).map((e) => e.nome),
    [empresas, selectedEmpresaIds]
  );

  const nomeObrasSelecionadas = useMemo(
    () => obras.filter((o) => selectedObraIds.includes(o.id)).map((o) => o.nome),
    [obras, selectedObraIds]
  );

  // A data de ruptura já é filtrada no backend (api/fichaRuptura); aqui só resta a busca por texto.
  const cpsFiltrados = useMemo(() => {
    let list = corposProva;
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter(
        (c) =>
          (c.etiqueta || '').toLowerCase().includes(q) ||
          (c.notaFiscal || '').toLowerCase().includes(q) ||
          String(c.idadeRuptura || '').includes(q) ||
          formatDateBr(c.dataConcretagem).includes(q)
      );
    }
    return list;
  }, [corposProva, query]);

  // Carga e tipo de ruptura saem em branco na ficha (preenchimento manual no laboratório).
  // Cálculo da carga esperada desativado por enquanto — a coluna sai em branco na ficha.
  const pdfData = useMemo(() => {
    return cpsFiltrados.map((cp) => ({
      id: cp.id,
      etiqueta: cp.etiqueta,
      idadeRuptura: cp.idadeRuptura,
      dataMoldagem: cp.dataConcretagem,
      obra: cp.obra,
      tensaoEsperada: null,
      idCaminhao: cp.idCaminhao,
      idTipoCorpoProva: cp.idTipoCorpoProva,
    }));
  }, [cpsFiltrados]);

  // Personalização do PDF: as assinaturas vêm da 1ª empresa selecionada.
  const primeiraEmpresa = empresas.find((e) => e.id === selectedEmpresaIds[0]);

  const {
    logoCliente,
    assinaturas,
    carregando: carregandoPersonalizacao,
  } = usePersonalizacaoRelatorio({
    idUsuario1: primeiraEmpresa?.idUsuarioAssinatura1,
    idUsuario2: primeiraEmpresa?.idUsuarioAssinatura2,
  });

  // Marca/desmarca uma empresa no filtro; ao desmarcar, também remove as obras dela.
  const toggleEmpresa = (id) => {
    setSelectedEmpresaIds((prev) => {
      const isSelected = prev.includes(id);
      if (isSelected) {
        // Ao desmarcar empresa, remove também as obras dela
        const obrasDeEmpresa = obras.filter((o) => o.idEmpresa === id).map((o) => o.id);
        setSelectedObraIds((prev2) => prev2.filter((oId) => !obrasDeEmpresa.includes(oId)));
        return prev.filter((eId) => eId !== id);
      }
      return [...prev, id];
    });
  };

  // Marca/desmarca uma obra no filtro (não auto-seleciona a empresa).
  const toggleObra = (id) => {
    // Selecionar obra NÃO auto-seleciona a empresa — ela aparece na lista sem check
    setSelectedObraIds((prev) => {
      const isSelected = prev.includes(id);
      if (isSelected) return prev.filter((oId) => oId !== id);
      return [...prev, id];
    });
  };

  // Define a data de ruptura, limpa os filtros de empresa/obra e fecha o calendário.
  const handleDateChange = (dateStr) => {
    setDataRuptura(dateStr);
    setSelectedEmpresaIds([]);
    setSelectedObraIds([]);
    if (dateStr) setIsCalendarOpen(false);
  };

  // Limpa a data de ruptura selecionada.
  const handleClearDate = () => {
    setDataRuptura('');
    setSelectedEmpresaIds([]);
    setSelectedObraIds([]);
    setIsCalendarOpen(true);
  };

  return (
    <div className="ficha-ruptura-page space-y-6">
      <section className="flex flex-col gap-4 rounded-2xl bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900 sm:text-3xl">Ficha de ruptura</h2>
          <p className="mt-2 max-w-2xl text-sm text-slate-500">
            Selecione a data de ruptura e filtre por empresa e obra para visualizar os corpos de prova.
          </p>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <p className="mb-3 text-xs uppercase tracking-[0.2em] text-slate-400">Data de ruptura</p>

        {/* Trigger — sempre visível */}
        <button
          type="button"
          onClick={() => setIsCalendarOpen((prev) => !prev)}
          className={`flex w-full max-w-xs items-center justify-between rounded-lg border px-3 py-2.5 text-sm transition ${
            dataRuptura
              ? 'border-[#0e7490] bg-[#0e7490]/5 text-slate-800'
              : 'border-slate-200 bg-slate-50 text-slate-400 hover:border-slate-300'
          }`}
        >
          <span className={dataRuptura ? 'font-medium' : ''}>
            {dataRuptura ? formatDateBr(dataRuptura) : 'Selecionar data de ruptura'}
          </span>
          <svg
            className={`h-4 w-4 flex-shrink-0 text-slate-400 transition-transform ${isCalendarOpen ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {dataRuptura && !isCalendarOpen ? (
          <button
            type="button"
            onClick={handleClearDate}
            className="mt-2 text-xs text-rose-500 transition hover:text-rose-700"
          >
            Limpar data
          </button>
        ) : null}

        {/* Calendário — aparece/some com animação */}
        {isCalendarOpen ? (
          <div className="mt-4">
            <CalendarioRuptura
              dataRuptura={dataRuptura}
              onChange={handleDateChange}
              onClear={handleClearDate}
              cpCountByDate={cpCountByDate}
              loadingCalendar={loadingCalendar}
            />
          </div>
        ) : null}
      </section>

      {dataRuptura ? (
        <section className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-4 py-3">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Empresas</p>
              <p className="mt-0.5 text-sm font-medium text-slate-700">
                {selectedEmpresaIds.length > 0
                  ? `${selectedEmpresaIds.length} selecionada${selectedEmpresaIds.length > 1 ? 's' : ''}`
                  : 'Selecione uma ou mais'}
              </p>
            </div>
            <div className="max-h-56 overflow-y-auto p-2">
              {empresasVisiveis.length === 0 ? (
                <p className="px-3 py-4 text-center text-sm text-slate-500">
                  Nenhuma empresa disponível para esta data.
                </p>
              ) : (
                empresasVisiveis.map((empresa) => (
                  <CheckboxItem
                    key={empresa.id}
                    label={empresa.nome}
                    checked={selectedEmpresaIds.includes(empresa.id)}
                    onChange={() => toggleEmpresa(empresa.id)}
                  />
                ))
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-4 py-3">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Obras</p>
              <p className="mt-0.5 text-sm font-medium text-slate-700">
                {selectedObraIds.length > 0
                  ? `${selectedObraIds.length} selecionada${selectedObraIds.length > 1 ? 's' : ''}`
                  : 'Selecione uma ou mais'}
              </p>
            </div>
            <div className="max-h-56 overflow-y-auto p-2">
              {obrasVisiveis.length === 0 ? (
                <p className="px-3 py-4 text-center text-sm text-slate-500">
                  Nenhuma obra disponível para esta data.
                </p>
              ) : (
                obrasVisiveis.map((obra) => (
                  <CheckboxItem
                    key={obra.id}
                    label={obra.nome}
                    checked={selectedObraIds.includes(obra.id)}
                    onChange={() => toggleObra(obra.id)}
                  />
                ))
              )}
            </div>
          </div>
        </section>
      ) : null}

      {dataRuptura ? (
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-4 py-3 sm:hidden">
            <ExpandableSearchFilter
              variant="block"
              query={query}
              onChange={setQuery}
              placeholder="Buscar por etiqueta, nota fiscal..."
              resultCount={cpsFiltrados.length}
            />
          </div>

          <div className="hidden overflow-x-auto sm:block">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-[0.3em] text-slate-400">
                <tr>
                  <th className="px-4 py-3">Etiqueta</th>
                  <th className="px-4 py-3">Data de concretagem</th>
                  <th className="px-4 py-3">Nota fiscal</th>
                  <th className="px-4 py-3">Idade (dias)</th>
                  <th className="px-4 py-3">
                    <ExpandableSearchFilter
                      variant="inline"
                      query={query}
                      onChange={setQuery}
                      placeholder="Buscar..."
                      resultCount={cpsFiltrados.length}
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {loadingCPs ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={`sk-${i}`} className="border-b border-slate-100">
                      {Array.from({ length: 5 }).map((__, j) => (
                        <td key={j} className="px-4 py-4">
                          <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : selectedObraIds.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-500">
                      Selecione pelo menos uma obra para visualizar os corpos de prova.
                    </td>
                  </tr>
                ) : cpsFiltrados.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-500">
                      Nenhum corpo de prova com data de ruptura em {formatDateBr(dataRuptura)}.
                    </td>
                  </tr>
                ) : (
                  cpsFiltrados.map((item) => (
                    <tr
                      key={item.id}
                      className="border-b border-slate-100 last:border-0 hover:bg-slate-50/80"
                    >
                      <td className="px-4 py-4 font-medium text-slate-900">{item.etiqueta}</td>
                      <td className="px-4 py-4 text-slate-600">{formatDateBr(item.dataConcretagem)}</td>
                      <td className="px-4 py-4 text-slate-600">{item.notaFiscal}</td>
                      <td className="px-4 py-4 text-slate-600">
                        {item.idadeRuptura !== '' && item.idadeRuptura != null
                          ? `${item.idadeRuptura} dias`
                          : '-'}
                      </td>
                      <td className="px-4 py-4" />
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="space-y-3 p-4 sm:hidden">
            {loadingCPs ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={`m-sk-${i}`} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="h-4 w-1/2 animate-pulse rounded bg-slate-100" />
                  <div className="mt-3 h-3 w-2/3 animate-pulse rounded bg-slate-100" />
                </div>
              ))
            ) : selectedObraIds.length === 0 ? (
              <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
                Selecione pelo menos uma obra para visualizar os corpos de prova.
              </div>
            ) : cpsFiltrados.length === 0 ? (
              <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
                Nenhum corpo de prova encontrado.
              </div>
            ) : (
              cpsFiltrados.map((item) => (
                <div key={item.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <p className="text-base font-semibold text-slate-900">{item.etiqueta}</p>
                  <div className="mt-3 grid grid-cols-1 gap-2 text-xs text-slate-600">
                    <span>Data de concretagem: {formatDateBr(item.dataConcretagem)}</span>
                    <span>Nota fiscal: {item.notaFiscal}</span>
                    <span>
                      Idade:{' '}
                      {item.idadeRuptura !== '' && item.idadeRuptura != null
                        ? `${item.idadeRuptura} dias`
                        : '-'}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="flex flex-col items-end gap-2 border-t border-slate-200 px-4 py-4">
            <p className="text-xs text-slate-400">
              As assinaturas do rodapé vêm dos usuários vinculados no cadastro da 1ª empresa selecionada.
            </p>
            <PDFDownloadLink
              document={(
                <FichaRupturaPDF
                  dataRuptura={formatDateBr(dataRuptura)}
                  resultados={pdfData}
                  empresas={nomeEmpresasSelecionadas}
                  obras={nomeObrasSelecionadas}
                  logoCliente={logoCliente}
                  assinaturas={assinaturas}
                />
              )}
              fileName={`ficha-ruptura-${dataRuptura}.pdf`}
              style={{
                textDecoration: 'none',
                pointerEvents: cpsFiltrados.length === 0 || carregandoPersonalizacao ? 'none' : 'auto',
              }}
              className={`inline-flex items-center gap-2 rounded-full bg-[#0e7490] px-5 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-[#0c6279] ${
                cpsFiltrados.length === 0 || carregandoPersonalizacao ? 'cursor-not-allowed opacity-50' : ''
              }`}
            >
              {({ loading }) => (carregandoPersonalizacao ? 'Preparando...' : loading ? 'Gerando...' : 'Gerar ficha de ruptura')}
            </PDFDownloadLink>
          </div>
        </section>
      ) : null}
    </div>
  );
}

export default FichaRupturaPage;
